import type { ColorPaletteSet } from './ColorPaletteSet.ts';
import type { EditorSettings } from './Editor.ts';
import { type SerializationContext, SerializationTypeError } from './errors.ts';
import { EventEmitter } from './EventEmitter.ts';
import { Logger } from './Logger.ts';
import { Modal } from './Modal.ts';
import { isItemGroup } from './MultiSelect.ts';
import { ObjectGroupItem, type ObjectGroupItemOptions, type ObjectGroupItemSerialized } from './ObjectGroupItem.ts';
import { PixelCanvas } from './canvas/PixelCanvas.ts';
import { Popover } from './Popover.ts';
import {
    findCanvas,
    findElement,
    findInput,
    findTemplateContent,
    parseTemplate,
    setTextAndTitle
} from './utils-dom.ts';
import {
    chars,
    clamp,
    generateId,
    get2dContext,
    type SiblingInsertOrder
} from './utils.ts';
import { ZoomControl } from './ZoomControl.ts';

export interface ObjectGroupOptions {
    id?: ObjectGroup['id'];
    name?: string;
    items?: ObjectGroupItem[];
    editorSettings: EditorSettings;
}

export interface ObjectGroupSerialized {
    id: string | number;
    name: ObjectGroup['name'];
    items: ObjectGroupItemSerialized[];
}

const objectGroupTmpl = `
<div class="project-item-group" data-drag-item="object-group">
    <div class="group-item project-list-item section-item">
        <div class="group-name-container">
            <i class="fa-solid fa-chevron-up collapse-icon"></i>
            <i class="icon fa-solid fa-grip" title="re-order this group" data-drag-handle></i>
            <header class="group-name clamp-1"></header>
        </div>
        <div class="item-controls">
            <button type="button" class="btn btn-sm btn-tertiary overflow-btn" title="More actions&hellip;">
                <i class="fa-solid fa-ellipsis-h"></i>
            </button>
        </div>
    </div>
    <div class="group-items" data-empty-drop-target="object-item"></div>
</div>
`;

const groupOverflowTmpl = `
<ul class="project-item-overflow list-unstyled dropdown-menu">
    <li class="dropdown-item"><a href="#" data-action="edit"><i class="fa-solid fa-fw fa-pencil icon"></i>Edit&hellip;</a></li>
    <li class="dropdown-item"><a href="#" data-action="add"><i class="fa-solid fa-fw fa-add icon"></i>New object</a></li>
    <li class="dropdown-item"><a href="#" data-action="animate"><i class="fa-solid fa-fw fa-film icon"></i>Animate&hellip;</a></li>
    <li class="dropdown-item divider"></li>
    <li class="dropdown-item"><a href="#" data-action="export-asm"><i class="fa-solid fa-fw fa-code icon"></i>Export ASM&hellip;</a></li>
    <li class="dropdown-item"><a href="#" data-action="export-images"><i class="fa-solid fa-fw fa-images icon"></i>Export spritesheet&hellip;</a></li>
    <li class="dropdown-item divider"></li>
    <li class="dropdown-item"><a href="#" data-action="delete" class="text-danger"><i class="fa-solid fa-fw fa-trash icon"></i>Delete&hellip;</a></li>
</ul>
`;

const editGroupTmpl = `
<form class="form-vertical">
    <div class="form-row">
        <input class="group-name-input form-control" type="text" maxlength="50" minlength="1" required />
    </div>
    <div class="submit-container">
        <button type="submit" class="btn btn-primary">Save</button>
    </div>
</form>
`;

export type ObjectGroupEventMap = {
    action_add: [];
    action_export_asm: [ Readonly<ObjectGroupItem[]> ];
    action_export_images: [ Readonly<ObjectGroupItem[]> ];
    name_change: [];
    item_activate: [ ObjectGroupItem ];
    item_clone: [{ original: ObjectGroupItem; newGroup: boolean }];
    item_remove: [ ObjectGroupItem ];
    item_add: [ ObjectGroupItem ];
    item_delete: [ ObjectGroupItem ];
    delete: [];
    zoom_level_change: [ number ];
}

export class ObjectGroup extends EventEmitter<ObjectGroupEventMap> {
    public readonly id: string;
    private name: string;
    private readonly logger: Logger;
    public readonly $itemContainer: HTMLElement;
    private readonly $el: HTMLElement;
    private readonly items: ObjectGroupItem[];
    private readonly editorSettings: EditorSettings;

    private static instanceCount = 0;

    public constructor(options: ObjectGroupOptions) {
        super();

        ObjectGroup.instanceCount++;
        this.id = options?.id || generateId();
        this.name = options?.name || `Group ${ObjectGroup.instanceCount}`;
        this.items = options?.items || [];
        this.$el = parseTemplate(objectGroupTmpl);
        this.$itemContainer = findElement(this.$el, '.group-items');
        this.editorSettings = options?.editorSettings;

        this.logger = Logger.from(this);
    }

    public getCanvases(): PixelCanvas[] {
        return this.items.map(item => item.canvas);
    }

    public getItems(): Readonly<ObjectGroupItem[]> {
        return this.items;
    }

    public hasItems(): boolean {
        return this.items.length > 0;
    }

    public getName(): string {
        return this.name;
    }

    public get activeItem(): ObjectGroupItem | null {
        return this.items.find(item => item.isActive) || null;
    }

    public setName(newName: string): void {
        newName = newName || `Group ${this.id}`;
        if (this.name === newName) {
            return;
        }

        this.name = newName;
        this.updateNameUI();
        this.emit('name_change');
    }

    private updateNameUI(): void {
        setTextAndTitle(findElement(this.$el, `.group-name`), this.name);
    }

    public createItem(options: Omit<ObjectGroupItemOptions, 'mountEl'>): ObjectGroupItem {
        return new ObjectGroupItem({
            ...options,
            mountEl: this.$itemContainer,
        });
    }

    public addItem(
        item: ObjectGroupItem,
        sibling: ObjectGroupItem | null = null,
        order: SiblingInsertOrder | null = null,
    ): boolean {
        const currentIndex = this.items.indexOf(item);
        const alreadyInGroup = currentIndex !== -1;

        let newIndex = sibling ? this.items.indexOf(sibling) : -1;

        if (!alreadyInGroup) {
            if (newIndex === -1) {
                this.logger.info(`adding ${item.name} to end`);
                this.items.push(item);
            } else {
                if (newIndex !== -1 && order !== 'before') {
                    newIndex++;
                    sibling = this.items[newIndex] || null;
                }
                this.logger.info(`adding ${item.name} at index ${newIndex}`);
                this.items.splice(newIndex, 0, item);
            }

            this.wireUpItem(item, newIndex !== -1 ? sibling : null);
            this.emit('item_add', item);
        } else {
            if (newIndex === -1) {
                this.logger.debug(`not doing anything to ${item.name}, already in group`);
            } else if (currentIndex !== newIndex) {
                this.logger.info(`moving ${item.name} from index ${currentIndex} to ${newIndex}`);
                this.items.splice(currentIndex, 1);
                this.items.splice(newIndex, 0, item);
            } else {
                this.logger.debug(`not doing anything to ${item.name}, already in correct position at index ${newIndex}`);
            }
        }

        this.logger.debug(`new item order:`,
            this.items.map(item => item.canvas.getName()).join(` ${chars.arrowRight} `));

        return true;
    }

    private wireUpItem(item: ObjectGroupItem, insertBefore: ObjectGroupItem | null = null): void {
        this.logger.debug(`wiring up item events for ${item.name}} (inserting before: ${insertBefore?.name || '[none]'})`);

        item.init(insertBefore);

        // Since items can change between groups, and groups are generally just an artificial structuring
        // of items/canvases with no real logic, groups should not listen to events on items/canvases
        // (because the group that an object belongs to can change frequently). Instead, the project should
        // listen to events directly from the item and/or canvas.

        // Or not, and just call item.off() every time and item switches groups, and then rebind all the events...
        item.off();

        item.on('delete', () => {
            if (this.removeItemFromArray(item)) {
                this.emit('item_delete', item);
            }
        });

        item.on('action_clone', ({ newGroup }) => {
            this.logger.info(`cloning ${item.name} into ${newGroup ? 'new group' : item.canvas.getGroup().getName()}`);
            this.emit('item_clone', {
                original: item,
                newGroup,
            });
        });
        item.on('action_export_asm', () => {
            this.emit('action_export_asm', [ item ]);
        });
        item.on('action_export_image', () => {
            this.emit('action_export_images', [ item ]);
        });
        item.on('activate', () => {
            this.emit('item_activate', item);
        });
        item.on('canvas_group_change', () => {
            // this shouldn't really be necessary, but it will handle the case where a canvas
            // manually sets its own group instead of going through the project/group (i.e. how
            // the UI works). essentially, if canvas.setGroup() is called outside the normal way.
            if (this.items.indexOf(item) === -1) {
                this.logger.warn(`item ${item.name} had its group change but is not part of the group, adding`);
                this.addItem(item);
            }
        });
    }

    private removeItemFromArray(item: ObjectGroupItem): boolean {
        const index = this.items.indexOf(item);
        if (index === -1) {
            this.logger.info(`attempted to remove ${item.name} from items but it's not present`);
            return false;
        }

        this.items.splice(index, 1);
        this.logger.info(`removed ${item.name} at index ${index}`);
        this.emit('item_remove', item);
        return true;
    }

    public moveItem(
        item: ObjectGroupItem,
        newGroup: ObjectGroup,
        sibling?: ObjectGroupItem | null,
        order?: SiblingInsertOrder | null,
    ): void {
        if (newGroup !== this && !this.removeItemFromArray(item)) {
            this.logger.warn(`trying to move item ${item.name} to new group, but it is not in group ${this.name}`);
        }

        // the order of these statements is important. adding the item removes the previous canvas event listeners,
        // so if you set the group before removing the listeners, the canvas.group_change event will cause the
        // group to re-add the item to its collection. which means both the old group and the new group
        // will have the item contained therein, which would be misfortunate.

        if (newGroup !== this) {
            this.logger.info(`moving ${item.name} to group ${newGroup.name}`);
            item.off();
        } else {
            this.logger.info(`re-ordering ${item.name} within the group`);
        }

        newGroup.addItem(item, sibling, order);
        item.setGroup(newGroup);
    }

    public deleteItem(item: ObjectGroupItem): boolean {
        this.logger.info(`deleting ${item.name}`);
        item.delete();
        return true;
    }

    public cloneItem(otherItem: ObjectGroupItem): ObjectGroupItem {
        const newItem = otherItem.clone(this);
        newItem.setGroup(this);
        this.addItem(newItem, otherItem, 'after');
        return newItem;
    }

    public delete(): void {
        this.logger.info(`deleting`);

        // deleteItem eventually splices the array, so the length of this.items can change
        // during the loop. making a copy of the array gets around that.
        const objects = this.items.concat([]);
        while (objects.length) {
            this.deleteItem(objects.pop()!);
        }

        this.$el.remove();
        this.emit('delete');
    }

    public setActiveItem(activeItem: ObjectGroupItem | null): void {
        if (activeItem === null) {
            // important that we don't deactivate an item that was just activated. activation
            // by an ObjectGroupItem emits "activate" which kind of recursively goes through
            // here. a little wonky, but meh. what's a little potential infinite recursion
            // between friends?
            this.activeItem?.deactivate();
        }

        this.items.forEach((item) => {
            if (item === activeItem) {
                item.activate();
            } else {
                item.deactivate();
            }
        });
    }

    public destroy(): void {
        this.delete();
    }

    public init($parent: HTMLElement): void {
        if (this.$el.parentNode === $parent) {
            return;
        }

        this.items.forEach(item => this.wireUpItem(item));

        const $group = this.$el;
        $group.setAttribute('data-group-id', this.id);
        this.updateNameUI();
        $parent.appendChild($group);

        const $overflowContent = parseTemplate(groupOverflowTmpl);
        const overflowPopover = new Popover({
            content: $overflowContent,
            dropdown: true,
        });
        const $overflowBtn = findElement($group, '.overflow-btn');

        const $editForm = parseTemplate(editGroupTmpl);
        const editPopover = new Popover({
            content: $editForm,
            title: 'Edit group',
            arrowAlign: 'left',
        });

        const $groupName = findElement($group, '.group-name');
        const $input = findInput($editForm, '.group-name-input');

        $editForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.setName($input.value);
            editPopover.hide();
        });

        $overflowContent.querySelectorAll('.dropdown-item a').forEach((anchor) => {
            anchor.addEventListener('click', (e) => {
                e.preventDefault();

                overflowPopover.hide();

                const action = anchor.getAttribute('data-action');
                switch (action) {
                    case 'edit':
                        $input.value = this.name;
                        editPopover.show($groupName);
                        $input.focus();
                        break;
                    case 'add':
                        this.emit('action_add');
                        break;
                    case 'delete': {
                        const count = this.items.length;
                        const modal = Modal.confirm(
                            {
                                title: `Delete ${this.name}`,
                                contentText: `Are you sure you want to delete this group` +
                                    (count === 1 ? ' and 1 object' : (count === 0 ? '' : ' and ' + count + ' objects')) +
                                    `? This cannot be undone.`,
                            },
                            () => this.delete(),
                        );

                        modal.show();
                        break;
                    }
                    case 'export-asm':
                        this.emit('action_export_asm', this.items);
                        break;
                    case 'export-images':
                        this.emit('action_export_images', this.items);
                        break;
                    case 'animate':
                        this.showAnimationModal();
                        break;
                }
            });
        });

        $overflowBtn.addEventListener('click', () => {
            const canvases = this.items.map(item => item.canvas);

            // disable "Export ASM" option if it's not supported by anything in the group
            const $exportAsm = findElement($overflowContent, '[data-action="export-asm"]');
            $exportAsm.classList.toggle('disabled', !canvases.some(canvas => canvas.canExportToASM()));

            // disable "Export spritesheet" and "Animate" options if there are less than two objects
            const $exportSpritesheet = findElement($overflowContent, '[data-action="export-images"]');
            const $animate = findElement($overflowContent, '[data-action="animate"]');
            [ $exportSpritesheet, $animate ].forEach(($el) => {
                $el.classList.toggle('disabled', canvases.length < 2);
            });

            overflowPopover.show($overflowBtn);
        });

        const $collapsible = findElement($group, '.group-name-container');
        $collapsible.addEventListener('click', () => {
            $group.classList.toggle('closed');
        });

        this.items.forEach(item => item.init());
    }

    public showAnimationModal(): void {
        const canvases = this.items.map(item => item.canvas);
        if (!canvases[0]) {
            return;
        }

        // see comment in project.showExportImagesModal() for why this is necessary
        canvases.forEach(canvas => canvas.render());

        let currentFrame = 0;
        const $tmpl = findTemplateContent(document, '#modal-content-animate-form');
        const $modalContent = $tmpl.cloneNode(true) as ParentNode;
        const $fpsInput = findInput($modalContent, '#animate-fps');
        const $preview = findCanvas($modalContent, 'canvas');
        const ctx = get2dContext($preview);

        const firstCanvas = canvases[0];
        const maxSize = 256; // NOTE: this should match the max size of .canvas-preview

        const $objectList = findElement($modalContent, '.animate-form-object-list');
        $objectList.innerHTML = '';
        $objectList.style.maxWidth = `${maxSize}px`;
        canvases.forEach((canvas, i) => {
            const $canvas = document.createElement('canvas');
            $canvas.setAttribute('title', `[${i}] ${canvas.getName()}`);
            $canvas.setAttribute('data-object-id', canvas.id);
            canvas.copyImageToCanvas($canvas, 48);
            $objectList.appendChild($canvas);
        });

        const zoomControl = new ZoomControl({
            context: 'export-images',
            editorSettings: this.editorSettings,
            $mount: findElement($modalContent, '.zoom-control'),
        });

        zoomControl.init();
        zoomControl.on('zoom_level_change', (newIndex) => {
            this.emit('zoom_level_change', newIndex);
            zoomControl.syncUI();
            canvases.forEach(canvas => canvas.render());
        });

        const maxFPS = Number($fpsInput.max) || 30;
        const minFPS = Number($fpsInput.min) || 1;

        let filteredCanvases: PixelCanvas[] = canvases.concat([]);

        const filterItems = PixelCanvas.getFilteredMultiSelectItems(
            canvases,
            findElement($modalContent, '.animate-form-object-filter'),
            () => {
                filteredCanvases = canvases.concat([])
                    .filter(canvas => filterItems.some(item => item.id === canvas.id && !isItemGroup(item) && item.selected));

                $objectList.querySelectorAll('[data-object-id]').forEach(($canvas) => {
                    if (!($canvas instanceof HTMLElement)) {
                        return;
                    }

                    const canvasId = $canvas.getAttribute('data-object-id') || '';
                    $canvas.style.display = filterItems.some(item => item.id === canvasId && !isItemGroup(item) && item.selected) ?
                        '' :
                        'none';
                });
            },
        );

        const drawFrame = () => {
            const canvas = filteredCanvases[currentFrame];

            // the dimensions might change due to zoom level so we need to calculate
            // this every frame
            const { width, height } = firstCanvas.getDisplayDimensions();
            const maxDimension = Math.max(width, height);

            const scale = maxDimension <= maxSize ? 1 : maxSize / maxDimension;

            const newWidth = width * scale;
            const newHeight = height * scale;

            if ($preview.width !== newWidth || $preview.height !== newHeight) {
                $preview.width = newWidth;
                $preview.height = newHeight;

                this.logger.debug(`animation preview set to ` +
                    `${$preview.width}x${$preview.height} (scale=${scale})`);
            }

            if (canvas) {
                ctx.clearRect(0, 0, $preview.width, $preview.height);
                canvas.drawBackgroundOnto(ctx, 0, 0, $preview.width, $preview.height);
                canvas.drawImageOnto(ctx, 0, 0, $preview.width, $preview.height);
                currentFrame = (currentFrame + 1) % filteredCanvases.length;
            } else {
                currentFrame = 0;
            }

            const lastFrameAt = Date.now();

            const wait = () => {
                const fps = clamp(minFPS, maxFPS, Number($fpsInput.value) || 0);
                if ($fpsInput.value !== fps.toString()) {
                    $fpsInput.value = fps.toString();
                }
                const nextFrameAt = lastFrameAt + ((1 / fps) * 1000);
                if (Date.now() >= nextFrameAt) {
                    drawFrame();
                    return;
                }

                window.requestAnimationFrame(wait);
            };

            wait();
        };

        const modal = Modal.create({
            type: 'default',
            title: `Animating ${this.name}`,
            actions: 'close',
            contentHtml: $modalContent,
        });

        modal.show();
        drawFrame();
        $fpsInput.focus();
    }

    public updateAllThumbnails(): void {
        this.items.forEach(item => item.updateThumbnail());
    }

    public updateAllThumbnailBackgrounds(): void {
        this.items.forEach(item => item.updateThumbnailBg());
    }

    public setZoomLevel(): void {
        this.items.forEach(item => item.canvas.setZoomLevel(item === this.activeItem));
    }

    public toJSON(): ObjectGroupSerialized {
        return {
            id: this.id,
            name: this.name,
            items: this.items.map(item => item.toJSON()),
        };
    }

    public static fromJSON(
        json: object,
        canvasMountEl: HTMLElement,
        settings: EditorSettings,
        paletteSets: Readonly<ColorPaletteSet[]>,
    ): ObjectGroup {
        const serialized = this.transformSerialized(json);

        const group = new ObjectGroup({
            id: String(serialized.id),
            name: serialized.name,
            editorSettings: settings,
        });

        const items = serialized.items.map(item =>
            ObjectGroupItem.fromJSON(item, group.$itemContainer, canvasMountEl, settings, group, paletteSets));

        items.forEach(item => group.addItem(item));
        return group;
    }

    public static transformSerialized(json: any): ObjectGroupSerialized {
        const context: SerializationContext = 'ObjectGroup';

        if (!json.id || (typeof json.id !== 'string' && typeof json.id !== 'number')) {
            throw new SerializationTypeError(context, 'id', 'non-empty string or number', json.id);
        }
        if (typeof json.name !== 'string') {
            throw new SerializationTypeError(context, 'name', 'string', json.name);
        }
        if (!Array.isArray(json.items) || !json.items.every((item: unknown) => typeof item === 'object')) {
            throw new SerializationTypeError(context, 'items', 'array of objects', json.items);
        }

        return json;
    }
}
